import React, { ChangeEvent, memo, useEffect } from 'react'

import Slider from '@material-ui/core/Slider'
import Box from '@material-ui/core/Box'
import makeStyles from '@material-ui/styles/makeStyles'

import VolumeDownIcon from '@material-ui/icons/VolumeDown'
import VolumeOffIcon from '@material-ui/icons/VolumeOff'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'

import { useBoolean } from '@guidde-co/shared.hooks.use-boolean'

const renderIcon = (value: number) => {
    switch (value) {
        case 0:
            return <VolumeOffIcon fontSize="inherit" color="inherit" />
        case 100:
            return <VolumeUpIcon fontSize="inherit" color="inherit" />
        default:
            return <VolumeDownIcon fontSize="inherit" color="inherit" />
    }
}

const useStyles = makeStyles({
    icon: {
        cursor: 'pointer',
        fontSize: '20px',
        minWidth: '23px',
        color: 'white'
    },
    sliderRoot: {
        padding: '5px 0'
    },
    sliderThumb: {
        boxShadow: 'none !important',
        '&:after': {
            top: '-2px !important',
            left: '-2px !important',
            right: '-2px !important',
            bottom: '-2px !important'
        }
    }
})

export type VolumeProps = {
    /**
     * a html video element from videoRef.current
     */
    videoElement: HTMLVideoElement | null
}

export const Volume = memo(({ videoElement }: VolumeProps) => {
    const classes = useStyles()
    const hover = useBoolean()
    const active = useBoolean()

    const [value, setValue] = React.useState<number>(100)

    const handleChange = (
        _event: ChangeEvent<{}>,
        newValue: number | number[]
    ) => {
        setValue(newValue as number)
        if (videoElement) {
            videoElement.volume = (newValue as number) / 100
        }
    }

    useEffect(() => {
        window.addEventListener('mouseup', active.setFalse)
        return () => window.removeEventListener('mouseup', active.setFalse)
    }, [active.setFalse])

    if (!videoElement) return null

    return (
        <Box
            display="flex"
            onMouseEnter={hover.setTrue}
            onMouseLeave={hover.setFalse}
            onMouseDown={active.setTrue}
            alignItems="center"
            mx={1}
        >
            <Box
                display="flex"
                className={classes.icon}
                onClick={() => {
                    setValue(value ? 0 : 100)
                    videoElement.volume = value ? 0 : 1
                }}
            >
                {renderIcon(value)}
            </Box>

            <Box
                mx={1}
                display={hover.isFalse && active.isFalse ? 'none' : 'flex'}
            >
                <Slider
                    value={value}
                    onChange={handleChange}
                    classes={{
                        root: classes.sliderRoot,
                        thumb: classes.sliderThumb
                    }}
                    style={{ width: '100px' }}
                />
            </Box>
        </Box>
    )
})
